import type { JSX, ReactNode, FormEvent, Ref, RefObject } from 'react';
import { useEffect, useRef } from 'react';
import type {
	FieldValues,
	SubmitErrorHandler,
	SubmitHandler,
} from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

import type * as models from 'pkg/api/models';

export const asString = (value: any) => value as string;
export const asNumber = (value: any) => Number.parseInt(value as string, 10);
export const asOptionalNumber = (value: any) =>
	value ? Number.parseInt(value as string, 10) : null;
export const asFloat = (value: any) => Number.parseFloat(value as string);
export const asNumberArray = (value: any) => value as number[];

export function submitForm(
	ref: RefObject<HTMLFormElement>,
	additionalParams?: { [key: string]: unknown }
): boolean {
	if (!ref.current) {
		return false;
	}

	const customEvent = new CustomEvent('submit', {
		cancelable: true,
		bubbles: true,
		detail: { additionalParams },
	});

	return ref.current.dispatchEvent(customEvent);
}

export type FormDataValue =
	| FormDataEntryValue
	| { [key: string]: FormDataEntryValue }
	| boolean
	| models.attachment.Attachment;

export interface FormPayload {
	[key: string]: FormDataValue;
}

interface FormProps {
	children: ReactNode;
	className?: string;
	action?: string;
	method?: string;
	testid?: string;
	onSubmit?: SubmitHandler<FieldValues>;
	onErrors?: SubmitErrorHandler<FieldValues>;
	formRef?: Ref<HTMLFormElement>;
	stopPropagation?: boolean;
	resetOnSubmit?: boolean;
}

const StyledForm = styled.form`
	label {
		font-weight: var(--font-weight-semibold);
	}
`;

const Form = ({
	children,
	className,
	action,
	method,
	testid,
	onSubmit = () => {},
	onErrors,
	formRef,
	stopPropagation = false,
	resetOnSubmit = false,
}: FormProps): JSX.Element => {
	const ref = useRef<HTMLFormElement>(null);
	const methods = useForm();

	useEffect(() => {
		if (resetOnSubmit && methods.formState.isSubmitSuccessful) {
			methods.reset();
		}
	}, [methods.formState, methods.reset]);

	if (!formRef) {
		formRef = ref;
	}

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		if (stopPropagation) {
			e.stopPropagation();
		}

		return methods.handleSubmit(onSubmit, onErrors)(e);
	};

	return (
		<FormProvider {...methods}>
			<StyledForm
				ref={formRef}
				action={action}
				method={method}
				noValidate
				className={className}
				data-testid={testid}
				onSubmit={handleSubmit}>
				{children}
			</StyledForm>
		</FormProvider>
	);
};

export default Form;
